import '@/components/proposals/proposal-animations.css';

import { useQueryClient } from '@tanstack/react-query';
import { LayoutLeft, LayoutRight } from '@untitledui/icons';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import AutoOpenFirstRecord from '@/components/proposals/AutoOpenFirstRecord';
import { InlineRecordPanelSkeleton } from '@/components/proposals/InlineRecordPanelSkeleton';
import ProposalDetailPanel from '@/components/proposals/ProposalDetailPanel';
import { ProposalNoAccessState } from '@/components/proposals/ProposalNoAccessState';
import ProposalListSidebar from '@/components/proposals/sidebar-list/ProposalListSidebar';
import { useAutoOpenTarget } from '@/components/proposals/useAutoOpenTarget';
import { getNextActionId } from '@/components/proposals/utils/getNextActionId';
import { flattenProposalsToDisplayOrder } from '@/components/proposals/utils/groupProposalsByTier';
import { useProposalViewHost } from '@/contexts/ProposalViewHostContext';
import { useSlideoutWidth } from '@/contexts/SlideoutWidthContext';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import {
  INLINE_PANEL_MAX_WIDTH,
  INLINE_PANEL_MIN_WIDTH,
  useCanHostInlinePanel,
} from '@/hooks/useCanHostInlinePanel';
import { Button } from '@/lib/base/buttons/button';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { SplitView } from '@/lib/base/split-view';
import { useSlideoutStackContextOptional } from '@/lib/crm/contexts/SlideoutStackContext';
import { InlineCrmObjectPanel } from '@/lib/crm/record/inline-crm-object-panel';
import type {
  ProposalDetail,
  ProposalListFilters,
  ProposalsListResponse,
} from '@/lib/proposals/core/types';
import { useProposalOptional } from '@/lib/proposals/hooks/useProposal';
import { proposalQueryKeys } from '@/lib/proposals/query/keys';
import { removeProposalFromLists } from '@/lib/proposals/query/listCacheUpdates';
import { useViewHealthMarker } from '@/lib/view-health';

/**
 * Minimum width (px) the action content needs before this page hosts the
 * entity panel inline (side-by-side). Below this the detail content would
 * squish, so the claim is dropped and the global overlay slideout takes
 * over — same as mobile.
 */
const MIN_ACTION_PAGE_WIDTH = 560;
/**
 * Width of the actions-list rail. Matches the legacy actions-centre list
 * pane so the list reads the same in both shells.
 */
const LIST_SIDEBAR_WIDTH = 380;
/**
 * Minimum action-content width required to keep BOTH side columns (rail +
 * record panel) open at once. Deliberately smaller than
 * MIN_ACTION_PAGE_WIDTH: with both columns up, the content may compress
 * further before the columns turn mutually exclusive — at the default record
 * panel width (600) this puts the coexist threshold at ~1684px viewport
 * instead of ~1764px, so full-screen laptop windows keep both columns.
 */
const MIN_FLANKED_ACTION_PAGE_WIDTH = 480;

// ── Keyed record-panel host ──────────────────────────────────────────────────
// Extracted so that action switches (key={actionId}) produce a clean remount:
//   • userClosed resets to false
//   • the SplitView presence machine initialises open (panel != null at mount)
//     with no enter animation — action switches are animation-free
//   • no stale stack entry from the previous action can be read at mount
// The outer (rail) SplitView stays un-keyed so the rail survives switches.

interface ActionRecordPanelSplitProps {
  action: ProposalDetail;
  /**
   * True while the actions-list rail is open AND the viewport cannot fit
   * rail + action content + record panel side by side (exclusive mode). The
   * panel hides — WITHOUT clearing the slideout stack, so closing the rail
   * (or the rail auto-closing when a trigger opens a record) reveals the
   * record exactly as it was. On wide viewports this is always false and the
   * two side columns coexist.
   */
  suppressPanel: boolean;
  onClose: () => void;
  onUpdate: (action: ProposalDetail) => void;
  onComplete: (precomputedNextActionId?: number) => Promise<void>;
  getNextActionId: (currentId: number) => number | null;
  headerLeading: ReactNode;
  isPlaceholderData: boolean;
}

function ActionRecordPanelSplit({
  action,
  suppressPanel,
  onClose,
  onUpdate,
  onComplete,
  getNextActionId: getNextActionIdProp,
  headerLeading,
  isPlaceholderData,
}: ActionRecordPanelSplitProps) {
  const { width: panelWidth, setWidth: setPanelWidth } = useSlideoutWidth();
  const slideoutStack = useSlideoutStackContextOptional();
  const clearStack = slideoutStack?.clearStack;
  const setStack = slideoutStack?.setStack;

  const canHostInline = useCanHostInlinePanel(MIN_ACTION_PAGE_WIDTH);

  // "What would populate the panel?" — shared with AutoOpenFirstRecord so the
  // two always agree on whether the action is still resolving or settled.
  // React Query dedupes the underlying trigger fetch. For an action whose
  // trigger data is already cached, this is SETTLED on the very first render.
  const autoOpenTarget = useAutoOpenTarget(action);

  // ── Switch veil (jitter fix) ─────────────────────────────────────────────
  // In-app action switches (rail row click, approve/dismiss advance) navigate
  // with `fromActionSwitch` in the location state INSTEAD of clearing the
  // slideout stack before navigating. A pre-navigation clearStack() commits
  // synchronously while the router navigation is still in a transition, so
  // the PREVIOUS keyed instance briefly saw an empty stack and started its
  // close sweep — the panel jittered "almost closed" and snapped back open.
  //
  // Instead, this freshly-keyed instance mounts with the previous action's
  // stack still present but VEILED: the stack reads as empty, so the panel
  // gate below evaluates against THIS action's derivation from the first
  // frame. A pre-paint layout effect then resolves the veil using whatever
  // the derivation already knows — nothing of the previous action, and no
  // speculative loading state, is ever painted:
  //   • settled ENTITY target (cached switch), rail NOT suppressing → seed the
  //     stack with it directly, so the record paints immediately (no skeleton
  //     frame; AutoOpenFirstRecord stands down against the seeded entry)
  //   • settled ENTITY target but rail suppressing the panel (exclusive mode)
  //     → clear, NOT seed: seeding would read as a record open and collapse
  //     the rail. Auto-open (gated on !suppressPanel) pushes once the rail is
  //     closed
  //   • settled with nothing / overlay target → clear; the gate stays closed
  //     from the first frame (no open-then-quickly-close flash)
  //   • still resolving (uncached) → clear; the gate holds the skeleton open
  //     and the auto-open pushes when it settles
  // `userClosed` never misreads the veil's stack write as a user action, and
  // AutoOpenFirstRecord (not mounted until the veil drops) only ever sees the
  // resolved stack. Deep links and back/forward carry no flag: no veil.
  const { state: locationState } = useLocation();
  const fromActionSwitch = Boolean(
    (locationState as { fromActionSwitch?: boolean } | null)?.fromActionSwitch
  );
  const [pendingSwitchClear, setPendingSwitchClear] =
    useState(fromActionSwitch);
  useLayoutEffect(() => {
    if (!pendingSwitchClear) {
      return;
    }
    // While the switch still shows placeholder (stale) data, `action` — and
    // therefore `autoOpenTarget` — is derived from the PREVIOUS action. Seeding
    // the stack from it here would put the old action's entity beside the new
    // one, and the keyed panel never remounts when fresh data arrives to
    // correct it. Hold the veil (stack reads empty) until the new action's data
    // has loaded, then resolve against its own derivation.
    if (isPlaceholderData) {
      return;
    }
    // In exclusive mode (rail open, viewport too narrow for both columns) the
    // rail owns the space and `suppressPanel` is true. Seeding an entity here
    // would land it as the topmost entity entry, which the rail-vs-record
    // arbiter below reads as "a record just opened" and closes the rail —
    // so switching actions from the rail would silently collapse it. Instead
    // clear the veiled stack and let `AutoOpenFirstRecord` (gated on
    // `!suppressPanel`) push the record once the rail is closed.
    //
    // Seeding via `setStack` does NOT bypass an active unsaved-changes guard:
    // the veil only runs on a keyed action switch (`fromActionSwitch` + new
    // `actionId`), which unmounts the previous action's `InlineCrmObjectPanel`
    // before this effect fires — and that unmount unregisters its push guard
    // (inline-crm-object-panel.tsx). By the time the seed executes there is no
    // record surface (and no guard) left to intercept, so there are never
    // unguarded unsaved changes to discard here.
    if (
      !suppressPanel &&
      autoOpenTarget.status === 'settled' &&
      autoOpenTarget.target?.kind === 'entity' &&
      setStack
    ) {
      setStack([{ kind: 'entity', id: autoOpenTarget.target.entityId }]);
    } else {
      clearStack?.();
    }
    setPendingSwitchClear(false);
  }, [
    pendingSwitchClear,
    clearStack,
    setStack,
    autoOpenTarget,
    isPlaceholderData,
    suppressPanel,
  ]);

  const stack = pendingSwitchClear ? [] : (slideoutStack?.stack ?? []);

  // Topmost ENTITY entry (ignoring email_thread overlays pushed above it).
  // Using filter + at(-1) rather than Array.prototype.findLast which is ES2023
  // and not included in our ES2022 tsconfig lib target.
  const entityEntries = stack.filter(e => e.kind === 'entity');
  const topEntityEntry = entityEntries.at(-1);

  const hasInlineContent = topEntityEntry != null;
  const isAwaiting = autoOpenTarget.status === 'resolving';
  const willPopulateInline =
    autoOpenTarget.status === 'settled' &&
    autoOpenTarget.target?.kind === 'entity';

  // userClosed: tracks that the user explicitly closed the record panel on this
  // action. Reset to false on every keyed remount (i.e. per action switch).
  //
  // Transition table:
  //   mount (keyed per action)                             → false (initial)
  //   stack's last entity entry disappears (X, Esc,
  //     trigger-toggle-off) while panel was open           → true
  //   skeleton chrome's close button (onClose)             → true
  //   entity entry (re)appears on stack (trigger click,
  //     deep link, browser forward) while panel is closed  → false  [REOPEN]
  //   (the actions rail never writes userClosed: on narrow
  //     viewports it hides the panel via `suppressPanel`
  //     without touching the stack, so closing the rail
  //     restores whatever was open)
  const [userClosed, setUserClosed] = useState(false);

  // React 19 previous-prop compare in render (REACT_19_PATTERNS § 4.2) —
  // detects entity entry appearing/disappearing to drive userClosed.
  // Never the forbidden `useEffect(() => setState(prop), [prop])` form.
  const [prevTopEntityEntry, setPrevTopEntityEntry] = useState(topEntityEntry);
  if (topEntityEntry !== prevTopEntityEntry) {
    setPrevTopEntityEntry(topEntityEntry);
    if (topEntityEntry != null && userClosed) {
      // Entity (re)appeared while panel was user-closed → reopen.
      setUserClosed(false);
    } else if (topEntityEntry == null && !userClosed) {
      // Entity disappeared while panel was open → treat as user close.
      setUserClosed(true);
    }
  }

  const handleSkeletonClose = useCallback(() => {
    setUserClosed(true);
  }, []);

  // The pending auto-open only fires when the stack is empty (AutoOpenFirstRecord
  // stands down whenever any entry is already on the stack). So the skeleton that
  // stands in for that pending open must ALSO require an empty stack — otherwise a
  // non-entity overlay (email-thread / proposal) sitting on the stack with no
  // entity beneath it would leave a permanent skeleton no push ever fills.
  const isStackEmpty = stack.length === 0;
  const showPendingAutoOpen =
    (isAwaiting || willPopulateInline) && isStackEmpty;

  // Panel is present when the viewport can host it, it isn't suppressed by
  // the rail in exclusive (narrow) mode, the user hasn't dismissed it, AND
  // there is content (or a pending auto-open to await). On wide viewports
  // `suppressPanel` is always false — rail and panel coexist. When the
  // derivation is already settled-empty at mount (cached data), the gate is
  // false from the first frame — no skeleton ever flashes.
  // Note: NO unconditional `pendingSwitchClear` term — during the veil the
  // gate already evaluates against this action's own derivation (veiled-empty
  // stack + isAwaiting/willPopulateInline), so a switch to an action that is
  // KNOWN to have nothing never opens the column at all.
  const showPanel =
    canHostInline &&
    !suppressPanel &&
    !userClosed &&
    (hasInlineContent || showPendingAutoOpen);

  // Panel content: topmost entity entry when one is on the stack; skeleton
  // while awaiting. Using the topmost entity (not the raw top-of-stack)
  // prevents the panel flashing to skeleton when an email_thread overlay is
  // pushed above an open record. Split across two variables to avoid a
  // nested ternary (no-nested-ternary ESLint rule).
  const inlinePanelContent =
    topEntityEntry != null ? (
      <InlineCrmObjectPanel entry={topEntityEntry} />
    ) : (
      <InlineRecordPanelSkeleton onClose={handleSkeletonClose} />
    );
  const inlinePanel = showPanel ? inlinePanelContent : null;

  return (
    <SplitView
      panel={inlinePanel}
      width={panelWidth}
      onWidthChange={setPanelWidth}
      resizable
      minWidth={INLINE_PANEL_MIN_WIDTH}
      maxWidth={INLINE_PANEL_MAX_WIDTH}
      resizeHandleLabel='Resize record panel'
      instance='action-detail'
    >
      <div className='h-full flex flex-col overflow-hidden min-h-0 min-w-0 relative'>
        {/* Auto-opens the first trigger record once per mount. The keyed
            parent already remounts per action so the key here is redundant
            for action-switches, but kept for the enabled-flip guard.
            `!userClosed` is threaded in so a target that resolves AFTER the
            user dismissed the skeleton does not push a record and reopen the
            panel — an explicit close stays closed until a trigger click. */}
        {/* Not mounted until the switch veil drops: its decision effect
            consumes a one-shot, and running it against the previous action's
            not-yet-cleared stack would consume it on stale state. */}
        {!pendingSwitchClear && (
          <AutoOpenFirstRecord
            key={`auto-open-${action.id}`}
            action={action}
            enabled={canHostInline && !suppressPanel && !userClosed}
          />
        )}
        <ProposalDetailPanel
          key={action.id}
          action={action}
          onClose={onClose}
          onUpdate={onUpdate}
          onComplete={onComplete}
          getNextActionId={getNextActionIdProp}
          headerLeading={headerLeading}
          variant='page'
        />
        {/* Loading overlay only when showing stale data from a DIFFERENT
            proposal (isPlaceholderData from keepPreviousData). */}
        {isPlaceholderData && (
          <div className='absolute inset-0 z-20 flex items-center justify-center bg-bg-primary/60 backdrop-blur-[1px]'>
            <LoadingIndicator type='line-spinner' size='md' />
          </div>
        )}
      </div>
    </SplitView>
  );
}

/**
 * Full-page view of a single AI action (/actions/:actionId). Owns its own
 * loading and error states — the list route never flashes underneath.
 */
export default function ActionDetailPage() {
  const { orgId, actionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const {
    data: fetchedAction,
    isPlaceholderData,
    isError: detailLoadFailed,
  } = useProposalOptional(
    orgId ? parseInt(orgId) : 0,
    actionId ? parseInt(actionId) : undefined
  ) as {
    data: ProposalDetail | undefined;
    isLoading: boolean;
    isPlaceholderData: boolean;
    isError: boolean;
  };

  // On mobile, skip placeholder (stale) data — it causes a flash of the
  // previous action's content with a loader overlay. Showing stale content on
  // a full-screen page is more jarring than a clean loading state.
  const action =
    actionId && !(isPlaceholderData && window.innerWidth < 1024)
      ? fetchedAction
      : undefined;

  // Hide the mobile nav header while a proposal page is open. The CSS media
  // query in design-system.css handles the mobile-only behavior.
  useEffect(() => {
    document.body.setAttribute('data-proposal-open', 'true');
    return () => {
      document.body.removeAttribute('data-proposal-open');
    };
  }, []);

  // Declare this route as a proposal-view host for its whole lifetime,
  // regardless of viewport. The record surface reads this to hide the reply
  // footer (the proposal IS the reply). Registered unconditionally — NOT gated
  // on `canHostInline` like the inline-host claim below — because the signal
  // must also reach the global overlay slideout that renders the record on
  // narrow/mobile viewports, which lives outside this page's subtree.
  const { registerProposalViewHost } = useProposalViewHost();
  useEffect(() => registerProposalViewHost(), [registerProposalViewHost]);

  // ── Inline entity panel registration ────────────────────────────────────
  // Entity slideouts render as a REAL CHILD of this page's SplitView — a
  // flex sibling of the detail content — instead of the global body-portaled
  // overlay. This page claims the `entity` kind via registerInlineHost, so
  // the global SlideoutRenderer stands down; the claim is dropped whenever
  // the viewport is too small (mobile, or a width where the detail content
  // would squish), and the global overlay takes over automatically.
  // Email-thread slideouts always overlay here; proposal slideouts overlay
  // on THIS page but dock inline on the chat page.
  //
  // Kept in the outer component (not in ActionRecordPanelSplit) so the claim
  // is active even while the action is loading — deep links with a
  // slideoutEntityId param still prefer the inline panel over the global overlay.
  const slideoutStack = useSlideoutStackContextOptional();
  const registerInlineHost = slideoutStack?.registerInlineHost;
  const clearStack = slideoutStack?.clearStack;

  const canHostInline = useCanHostInlinePanel(MIN_ACTION_PAGE_WIDTH);

  useEffect(() => {
    if (!canHostInline || !registerInlineHost) {
      return;
    }
    return registerInlineHost(['entity']);
  }, [canHostInline, registerInlineHost]);

  // ── Actions-list sidebar ────────────────────────────────────────────────
  // An opt-in rail on the LEFT carrying the same ProposalListPanel the list
  // route renders, so you can move between actions without going back to the
  // list. Off by default: this route's whole point is the full-width action.
  // Whether rail + action content + record panel ALL fit side by side (the
  // same geometry hook as `canHostInline`, with the rail's width added to the
  // content minimum). Wide viewports → the two side columns coexist. Narrow
  // viewports → exclusive mode: the open rail suppresses the panel (without
  // clearing the stack), and a record opening closes the rail (below).
  const canFitBothSidebars = useCanHostInlinePanel(
    MIN_FLANKED_ACTION_PAGE_WIDTH + LIST_SIDEBAR_WIDTH
  );

  const [isListSidebarOpen, setIsListSidebarOpen] = useState(false);
  const toggleListSidebar = useCallback(() => {
    setIsListSidebarOpen(open => !open);
  }, []);

  const suppressPanel = isListSidebarOpen && !canFitBothSidebars;

  // Exclusive mode, other direction: a record OPENING while the rail is up
  // (a trigger-button click, an entity badge) closes the rail so the panel
  // can show — without this, clicking "View X" with the rail open would
  // appear to do nothing. Previous-value compare in render (REACT_19_PATTERNS
  // § 4.2), keyed on the topmost entity entry's identity so switching to a
  // DIFFERENT record while suppressed also closes the rail. Wide viewports
  // never take this branch — rail and panel coexist there.
  const stackEntityEntries = (slideoutStack?.stack ?? []).filter(
    e => e.kind === 'entity'
  );
  const topEntityEntry = stackEntityEntries.at(-1);
  const [prevTopEntityEntry, setPrevTopEntityEntry] = useState(topEntityEntry);
  if (topEntityEntry !== prevTopEntityEntry) {
    setPrevTopEntityEntry(topEntityEntry);
    if (topEntityEntry != null && isListSidebarOpen && !canFitBothSidebars) {
      setIsListSidebarOpen(false);
    }
  }

  const isLargeScreen = useBreakpoint('lg');

  // Below `lg` there isn't room for a rail beside the action content, so the
  // toggle is hidden and any open rail is dropped on the way down.
  useEffect(() => {
    if (!isLargeScreen && isListSidebarOpen) {
      setIsListSidebarOpen(false);
    }
  }, [isLargeScreen, isListSidebarOpen]);

  // Back target: the list view the user came from (carried in navigation
  // state by the list's row click), falling back to the Open list for deep
  // links. Deliberately NOT navigate(-1): opening an entity slideout on this
  // page pushes a `?slideoutEntityId=` history entry and closing it replaces,
  // so history-walking lands back on this same action URL instead of the
  // list. Declared here because the rail's row-select carries it forward too.
  const backPath = (location.state as { backPath?: string } | null)?.backPath;

  // Selecting a row in the rail swaps the action in place — same route, new
  // id — so the rail stays open and mounted across the change. The backPath
  // rides along so the chevron still returns to the list you came from.
  // Deliberately NO clearStack() here: navigation commits in a router
  // transition, so a synchronous pre-navigation clear briefly re-renders the
  // OUTGOING keyed instance with an empty stack and starts its close sweep —
  // the panel jitter. `fromActionSwitch` tells the incoming instance to veil
  // and clear the stale stack itself, pre-paint (see ActionRecordPanelSplit).
  const handleSidebarSelect = useCallback(
    (selectedId: number) => {
      if (orgId) {
        navigate(`/${orgId}/actions/${selectedId}`, {
          state: { fromActionSwitch: true, ...(backPath ? { backPath } : {}) },
        });
      }
    },
    [navigate, orgId, backPath]
  );

  // `panel={null}` when closed is what drives SplitView's close sweep — the
  // machine animates out on the panel going away, rather than us unmounting
  // the column ourselves.
  const listSidebarPanel = isListSidebarOpen ? (
    <div className='h-full flex flex-col min-h-0 overflow-hidden'>
      <ProposalListSidebar
        selectedActionId={actionId ? parseInt(actionId) : undefined}
        onActionSelect={handleSidebarSelect}
      />
    </div>
  ) : null;

  const listSidebarToggle = isLargeScreen ? (
    <Button
      color='tertiary'
      size='sm'
      iconLeading={isListSidebarOpen ? LayoutLeft : LayoutRight}
      onClick={toggleListSidebar}
      className='flex-shrink-0'
      aria-label={isListSidebarOpen ? 'Hide actions list' : 'Show actions list'}
      aria-pressed={isListSidebarOpen}
    />
  ) : null;

  useViewHealthMarker('proposal-detail-ready', {
    ready: Boolean(action),
  });

  // ── Handlers ───────────────────────────────────────────────────────────
  const handleUpdate = useCallback(
    (updatedAction: ProposalDetail) => {
      if (orgId && actionId) {
        queryClient.setQueryData(
          proposalQueryKeys.detail(parseInt(orgId), parseInt(actionId)),
          updatedAction
        );
      }
    },
    [orgId, actionId, queryClient]
  );

  // "Which action comes next" for the approve/dismiss advance (task 12):
  // read the cached Open-list query and apply the shared display-order rule
  // (utils/getNextActionId: neighbour below, fallback above). The Open list
  // is the only list a pending action lives in, so "next" is always defined
  // against it — deliberately including when the user arrived from a
  // filtered view. Cache-only (no fetch): on a deep link with no list cache
  // this returns null and resolving falls back to the list, same as before.
  const getNextActionIdFromList = useCallback(
    (currentId: number): number | null => {
      if (!orgId) {
        return null;
      }
      const numericOrgId = parseInt(orgId);
      const listEntries = queryClient.getQueriesData<ProposalsListResponse>({
        queryKey: proposalQueryKeys.lists(numericOrgId),
      });
      for (const [key, data] of listEntries) {
        // Key shape: ['proposals', orgId, 'list', filters?] — pick the
        // unfiltered pending (Open) list.
        const filters = key[3] as ProposalListFilters | undefined;
        if (
          !data?.results?.length ||
          filters?.resolution !== 'pending' ||
          !filters?.assigned_to_me ||
          filters?.filter_tags__contains
        ) {
          continue;
        }
        const flattened = flattenProposalsToDisplayOrder(data.results);
        const nextId = getNextActionId(
          flattened,
          currentId,
          new Set([currentId])
        );
        if (nextId !== null) {
          return nextId;
        }
      }
      return null;
    },
    [orgId, queryClient]
  );

  const handleComplete = useCallback(
    async (precomputedNextActionId?: number) => {
      if (!orgId || !actionId) {
        return;
      }
      const numericOrgId = parseInt(orgId);
      const numericActionId = parseInt(actionId);

      // Remove the action from the cached lists immediately for snappy UX
      removeProposalFromLists(queryClient, numericOrgId, numericActionId);

      // Clear the completed action's detail from cache BEFORE navigating.
      // Using setQueryData(undefined) instead of removeQueries because
      // keepPreviousData still shows stale data after removeQueries.
      queryClient.setQueryData(
        proposalQueryKeys.detail(numericOrgId, numericActionId),
        undefined
      );

      // Advance to the next action in list display order (task 12) —
      // carrying backPath forward so the chevron still returns to the list
      // the user came from. Desktop only, mirroring the list panel's own
      // advance gate (useBulkResolution): on small screens the full-page
      // detail is the whole viewport and auto-advancing is disorienting.
      // Fall back to the list when there is nothing to advance to (last
      // action resolved, or a deep link with no cached list).
      // Navigating to another action: NO pre-navigation clearStack() — the
      // incoming keyed instance veils and clears the stale stack itself
      // (`fromActionSwitch`), so the outgoing panel never starts a close sweep
      // (the jitter fix).
      if (precomputedNextActionId != null && window.innerWidth >= 1024) {
        navigate(`/${orgId}/actions/${precomputedNextActionId}`, {
          state: { fromActionSwitch: true, ...(backPath ? { backPath } : {}) },
        });
      } else {
        // Falling back to the list: there is no incoming keyed action instance
        // to run the veil cleanup, so clear the stack here. Otherwise the
        // global slideout renderer keeps the just-completed record open through
        // the route transition to the list.
        clearStack?.();
        navigate(`/${orgId}/actions`);
      }

      // Mark list caches stale WITHOUT refetching. Approval submits in the
      // background (submitApprovalInBackground), so an eager refetch here
      // races the server-side commit: the response still contains the
      // just-approved proposal, resurrecting the optimistically-removed row
      // for a beat until the confirmation path's invalidate removes it again.
      queryClient.invalidateQueries({
        queryKey: proposalQueryKeys.lists(numericOrgId),
        refetchType: 'none',
      });
    },
    [orgId, actionId, navigate, queryClient, backPath, clearStack]
  );

  const handleClose = useCallback(() => {
    if (backPath) {
      navigate(backPath);
    } else if (orgId) {
      navigate(`/${orgId}/actions`);
    }
  }, [backPath, navigate, orgId]);

  return (
    <div className='page-enter flex flex-col h-full min-h-0 overflow-hidden bg-bg-primary'>
      {/* Actions-list rail on the LEFT, wrapping the record-panel SplitView.
          Nesting two SplitViews rather than hand-rolling the rail gets the
          same presence machine and width sweep the record panel already
          uses, so both sides animate identically.

          The rail renders the LEGACY list panel deliberately: the redesigned
          grouped list is a full-width table (bulk controls, per-band
          headers), which does not read in a 380px rail. The legacy panel was
          built for exactly this narrow sidebar. */}
      <SplitView
        side='left'
        panel={listSidebarPanel}
        width={LIST_SIDEBAR_WIDTH}
        resizeHandleLabel='Resize actions list'
        instance='actions-list-rail'
      >
        {/* Keyed by actionId: clean remount on every action switch so that
            userClosed resets, the SplitView opens without an enter animation
            (panel != null at mount → starts in 'open' state), and no stale
            stack entry from the previous action can be read. */}
        {action && (
          <ActionRecordPanelSplit
            key={actionId}
            action={action}
            suppressPanel={suppressPanel}
            onClose={handleClose}
            onUpdate={handleUpdate}
            onComplete={handleComplete}
            getNextActionId={getNextActionIdFromList}
            headerLeading={listSidebarToggle}
            isPlaceholderData={isPlaceholderData}
          />
        )}
        {!action && detailLoadFailed && !isPlaceholderData && (
          <ProposalNoAccessState onBack={handleClose} />
        )}
        {!action && !detailLoadFailed && (
          <div className='flex h-full items-center justify-center'>
            <LoadingIndicator type='line-spinner' size='md' />
          </div>
        )}
      </SplitView>
    </div>
  );
}
